import React, { useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useSoulBackend } from "backend";
import { useUrlParams } from "hooks/useUrlParams";
import { stripePromise } from "utils/stripe";
import ButtonDialog from "default/components/ButtonDialog";
import Loading from "default/components/Loading";

import { COLORS } from "constants/colors";
import AppLayout from "default/components/AppLayout";
import Error from "default/components/Error";
import InputEmail from "default/components/InputEmail";
import PayPayButton from "default/components/PayPayButton";
import Footer from "default/components/Footer";
import StyledButton from "default/components/StyledButton";
import TicketBox from "default/components/TicketBox";
import { lineAccountLinks } from "constants/links";

// 邪神ちゃんドロップキックの購入画面。App（/jashindp）と System（/jashindk）の両方で使う。
// データは useSoulBackend()、見た目と文言は contentWork（jashindp）から引く。
function Tickets() {
  const navigate = useNavigate();
  const { soulId, lng, work, contentWork, character } = useUrlParams();
  const { t } = useTranslation([contentWork, "common"]);
  const backend = useSoulBackend();

  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [unconfirmedPayment, setUnconfirmedPayment] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [isError, setIsError] = useState(false);
  const [emailRequired, setEmailRequired] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [purchaseMethod, setPurchaseMethod] = useState(null);

  // PayPay は「アプリを閉じないで」の確認画面を挟む
  const purchaseWithPayPay = (ticket) => {
    const query = new URLSearchParams({
      soul_id: soulId,
      lng,
      ticket_key: ticket.ticket_key,
      ...(ticket.episode_id ? { episode_id: ticket.episode_id } : {}),
      ...(ticket.price != null ? { price: String(ticket.price) } : {}),
    });
    navigate(`/${work}/confirm-paypay?${query}`);
  };

  const purchaseWithStripe = async (ticket) => {
    try {
      const { session_id } = await backend.purchaseWithStripe(soulId, ticket);
      const stripe = await stripePromise;
      // Stripe Checkoutページを開く
      const { error } = await stripe.redirectToCheckout({
        sessionId: session_id,
      });
      if (error) {
        console.error("Error:", error);
      }
    } catch (error) {
      console.error("Error:", error);
    }
    setIsProcessing(false);
  };

  const purchase = (method, ticket) => {
    if (method === "stripe") {
      purchaseWithStripe(ticket);
    } else if (method === "paypay") {
      purchaseWithPayPay(ticket);
    }
  };

  const onClickPurchaseMethod = async (method) => {
    setIsProcessing(true);
    try {
      const email = await backend.getEmail();

      if (!email.email_address) {
        window.scrollTo(0, 0);
        setPurchaseMethod(method);
        setOpenDialog(false);
        setEmailRequired(true);
        setIsProcessing(false);
        return;
      }
    } catch (error) {
      console.error("Error:", error);
      setIsProcessing(false);
      return;
    }

    purchase(method, selectedTicket);
  };

  const onClickPurchase = (ticket) => {
    setSelectedTicket(ticket);
    setOpenDialog(true);
  };

  const initTickets = async () => {
    try {
      setUnconfirmedPayment(await backend.getUnconfirmedPaypay(soulId));
    } catch (error) {
      console.error("Error:", error);
    }

    try {
      setTickets(await backend.listTickets(soulId));
    } catch (error) {
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const listOtherAccountLinks = () =>
    Object.entries(lineAccountLinks[contentWork]?.[lng] || {})
      .filter(([key]) => key !== character)
      .map(([name, url]) => ({ name, url }));

  // 初回ロード時
  useEffect(() => {
    initTickets();
  }, [soulId, backend]);

  if (isLoading) {
    return (
      <AppLayout>
        <Loading />
      </AppLayout>
    );
  }

  if (isError) {
    return (
      <AppLayout>
        <Error />
      </AppLayout>
    );
  }

  if (emailRequired) {
    return (
      <AppLayout>
        <InputEmail
          updateEmailFn={backend.updateEmail}
          onClickPurchase={() => purchase(purchaseMethod, selectedTicket)}
        />
      </AppLayout>
    );
  }

  // 続けて買うチケットがあるとき（期限切れのあと）は Epilogue を出す
  const isRepurchase = tickets.some((ticket) => ticket.is_continuation);
  const storyLines = t(
    `${contentWork}:${isRepurchase ? "epilogue" : "story"}.${character}`,
    { returnObjects: true }
  );

  const PurchaseDialog = ({ open, onClose, onPurchaseMethod, isProcessing }) => {
    const stripeButton = (
      <StyledButton
        variant="contained"
        onClick={() => onPurchaseMethod("stripe")}
        sx={{
          width: "100%",
          fontSize: "16px",
          background: isProcessing ? "#ffffff" : "transparent",
        }}
        disabled={isProcessing}
      >
        {t("common:button.purchase_option_credit_card")}
      </StyledButton>
    );

    const payPayBox = (
      <Box sx={{ width: "100%" }}>
        {lng === "ja" && (
          <PayPayButton
            onClick={() => onPurchaseMethod("paypay")}
            disabled={isProcessing}
          />
        )}

        <Typography
          sx={{
            mt: 1,
            fontSize: "12px",
            color: COLORS.TEXT_BLACK,
            textAlign: "center",
            fontFamily: '"Noto Serif JP", serif',
            whiteSpace: "pre-line",
          }}
        >
          {lng === "ja" && t("common:button.purchase_option_paypay")}
        </Typography>
      </Box>
    );

    const rawButtons =
      lng === "ja" ? [stripeButton, payPayBox] : [stripeButton];

    return (
      <ButtonDialog
        open={open}
        title={`${t("common:button.dialog.select_purchase")}`}
        rawButtons={rawButtons}
        handleClose={onClose}
      />
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        backgroundImage: `url(/${contentWork}/background.png)`,
        backgroundSize: "100% auto",
        backgroundRepeat: "repeat",
        backgroundPosition: "top center",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 5,
          width: "100%",
          mb: 5,
        }}
      >
        <Box sx={{ position: "relative", width: "100%", overflow: "hidden" }}>
          {/* メイン画像 */}
          <img
            src={`/${contentWork}/hero_image.png`}
            alt="hero image"
            width="100%"
            style={{ display: "block" }} // 隙間をなくす
          />
        </Box>

        {/* Story */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            width: "100%",
          }}
        >
          <Typography
            sx={(theme) => ({
              ...theme.components.sectionHeader,
            })}
          >
            {isRepurchase ? "Epilogue" : "Story"}
          </Typography>

          <Typography
            variant="h4"
            sx={(theme) => ({
              mt: 1,
              ...theme.components.storySection.name,
            })}
          >
            - {t(`${contentWork}:character.${character}`)} -
          </Typography>

          <Typography
            sx={(theme) => ({
              mt: 2,
              textAlign: "center",
              letterSpacing: "0.1em",
              ...theme.components.storySection.story,
            })}
          >
            {Array.isArray(storyLines) &&
              storyLines.map((line, index) => (
                <React.Fragment key={index}>
                  {line}
                  {index !== storyLines.length - 1 && <br />}
                </React.Fragment>
              ))}
          </Typography>
        </Box>

        <Typography
          sx={(theme) => ({
            mt: 8,
            ...theme.components.sectionHeader,
          })}
        >
          Ticket
        </Typography>
        {tickets.length === 0 && (
          <Typography
            sx={(theme) => ({
              whiteSpace: "pre-line",
              lineHeight: 1.8,
              fontFamily: theme.system.fonts.body,
              color: theme.system.colors.text.primary,
              fontSize: theme.system.fontSizes.medium,
            })}
          >
            {t("common:error.ticket_not_available")}
          </Typography>
        )}

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 5,
            width: "100%",
          }}
        >
          {tickets.map((ticket) => (
            <Box key={ticket.ticket_key} sx={{ width: "100%" }}>
              <TicketBox
                ticket={ticket}
                purchaseLabel={t("common:button.purchase_ticket_named", {
                  name: ticket.name,
                })}
                onClick={() => onClickPurchase(ticket)}
              />
            </Box>
          ))}
        </Box>
      </Box>

      {/* Others Section */}
      <Box
        sx={{
          width: "100%",
          py: 8,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          mt: 6,
        }}
      >
        <Typography
          sx={(theme) => ({
            letterSpacing: "0.06em",
            mb: 3,
            ...theme.components.sectionHeader,
          })}
        >
          Others
        </Typography>
        {listOtherAccountLinks().map((char, idx, arr) => (
          <Typography
            key={char.name}
            component="a"
            href={char.url}
            sx={{
              color: COLORS.TEXT_RED,
              fontFamily: '"Noto Serif JP", serif',
              fontSize: "24px",
              fontWeight: "bold",
              textDecoration: "none",
              mb: idx === arr.length - 1 ? 0 : 2,
              cursor: "pointer",
              transition: "opacity 0.2s",
              "&:hover": { opacity: 0.7 },
            }}
          >
            {t(`${contentWork}:character.${char.name}`)}
          </Typography>
        ))}
      </Box>

      <Footer />

      <ButtonDialog
        open={Boolean(unconfirmedPayment)}
        title={t("common:PayPay.unconfirmed_notice")}
        handleClose={() => setUnconfirmedPayment(null)}
        buttons={[
          {
            label: t("common:PayPay.check_status"),
            onClick: () => {
              const query = new URLSearchParams({
                soul_id: soulId,
                lng,
                merchant_payment_id: unconfirmedPayment.merchant_payment_id,
                ...(unconfirmedPayment.purchase_type
                  ? { purchase_type: unconfirmedPayment.purchase_type }
                  : {}),
                ...(unconfirmedPayment.ticket_key
                  ? { ticket_key: unconfirmedPayment.ticket_key }
                  : {}),
              });
              navigate(`/${work}/check-paypay?${query}`);
            },
          },
        ]}
      />

      <PurchaseDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        onPurchaseMethod={onClickPurchaseMethod}
        isProcessing={isProcessing}
      />
    </Box>
  );
}

export default Tickets;
